import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  CoverageFlatTeamSettingGroupById,
  type CoverageFlatTeamSettingGroupId,
} from "./groups";

type CoverageSettingInput = SettingRegistryMetadataInput;

function setting(metadata: CoverageSettingInput): CoverageSettingInput {
  return metadata;
}

function coverageGroup<
  const Group extends CoverageFlatTeamSettingGroupId,
  const T extends Record<string, CoverageSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const CoverageRegistry = {
  ...coverageGroup("coverage.custom_logo", {
    "shopifyExtension/logo": setting({
      label: "Custom logo",
      description:
        "Image shown next to the return coverage toggle in the customer cart.",
      synonyms: ["coverage logo", "cart logo", "cart toggle logo"],
    }),
    "shopifyExtension/displayIcon": setting({
      label: "Add icon on logo",
      description:
        "Show a badge icon on the return coverage toggle instead of, or alongside, the custom logo.",
      synonyms: ["coverage icon", "badge icon", "toggle icon"],
    }),
    "shopifyExtension/icon": setting({
      label: "Coverage toggle icon",
      description:
        "Which badge icon (gift, shield, or lock) appears on the return coverage toggle.",
      synonyms: ["gift icon", "shield icon", "lock icon"],
    }),
    "shopifyExtension/iconColor": setting({
      label: "Icon color",
      description: "Foreground color of the coverage toggle badge icon.",
      synonyms: ["icon foreground color"],
    }),
    "shopifyExtension/iconBackgroundColor": setting({
      label: "Icon background color",
      description: "Background color behind the coverage toggle badge icon.",
      synonyms: ["icon fill color", "badge background"],
    }),
    "shopifyExtension/redoProductImage": setting({
      label: "Custom product image",
      description:
        "Image used for the coverage product line item in the customer cart.",
      synonyms: ["cart product image", "coverage product image"],
    }),
  }),
  ...coverageGroup("coverage.info_modal", {
    "infoModal/sideImage": setting({
      label: "Info modal side image",
      description: "Left-side image shown in the coverage info modal.",
      synonyms: ["modal left image", "modal side image"],
    }),
    "infoModal/logo": setting({
      label: "Info modal logo",
      description: "Logo shown in the coverage info modal header.",
      synonyms: ["modal logo"],
    }),
    "infoModal/headerText": setting({
      label: "Headline text",
      description: "Headline shown at the top of the coverage info modal.",
      synonyms: ["modal headline", "modal title"],
    }),
    "infoModal/subheaderText": setting({
      label: "Body text",
      description:
        "Body copy shown under the headline in the coverage info modal.",
      synonyms: ["modal body text", "modal subheading"],
    }),
    "infoModal/cartIconEnabled": setting({
      label: "Enable for cart toggle",
      description:
        "Show the info icon on the cart coverage toggle that opens this modal.",
      synonyms: ["cart info icon", "cart modal toggle"],
    }),
    "infoModal/checkoutIconEnabled": setting({
      label: "Enable for checkout checkbox",
      description:
        "Show the info icon on the checkout coverage checkbox that opens this modal.",
      synonyms: ["checkout info icon", "checkout modal toggle"],
    }),
    "infoModal/customCss": setting({
      label: "Custom CSS",
      description: "Custom CSS applied to the coverage info modal.",
      synonyms: ["modal custom css", "modal styling"],
    }),
    "infoModal/enableTopLayerModalApi": setting({
      label: "Use Top Layer API",
      description:
        "Position the info modal with the browser Top Layer API instead of z-index.",
      synonyms: ["top layer modal", "modal positioning"],
    }),
  }),
  ...coverageGroup("coverage.general_coverage", {
    hideCheckbox: setting({
      label: "Show on product page",
      description:
        "Show the Redo return coverage info on the product detail page.",
      synonyms: [
        "show on product page",
        "product page coverage",
        "hide checkbox",
      ],
      valueSemantics: {
        summary:
          "Stored inverted from its label: true hides the coverage info on the product page.",
        trueLabel: "Hidden from the product page",
        falseLabel: "Shown on the product page",
      },
    }),
    "shopifyExtension/coverageExcludePickup": setting({
      label: "Exclude in-store pickup",
      description:
        "Do not add coverage for in-store pickup orders on your Shopify store (checkout checkbox only).",
      synonyms: ["exclude in-store pickup", "in store pickup coverage"],
    }),
  }),
  ...coverageGroup("coverage.exclusions", {
    discountCodePartialMatch: setting({
      label: "Require exact match for discount codes",
      description:
        "Require an exact discount-code match (rather than a partial match) when excluding coverage by discount code.",
      synonyms: ["require exact match", "discount code partial match"],
      valueSemantics: {
        summary:
          "Stored inverted from its label: true matches discount codes partially, which is exact-match off.",
        trueLabel: "Partial discount-code matches exclude coverage",
        falseLabel: "Only exact discount-code matches exclude coverage",
      },
    }),
    coverageExcludeTags: setting({
      label: "Exclude order tags",
      description:
        "Coverage will not be added for orders with these Shopify order tags.",
      synonyms: ["exclude order tags", "order tag exclusions"],
    }),
    coverageExcludeSources: setting({
      label: "Exclude sources",
      description:
        "Coverage will not be added for orders from these sources (e.g. POS).",
      synonyms: ["exclude sources", "source exclusions"],
    }),
    coverageExcludeCustomerTags: setting({
      label: "Exclude customer tags",
      description:
        "Coverage will not be offered to customers with these Shopify tags.",
      synonyms: ["exclude customer tags", "customer tag exclusions"],
    }),
    coverageExcludeProperties: setting({
      label: "Exclude properties",
      description:
        "Coverage will not be offered for items with these line-item properties or values.",
      synonyms: ["exclude properties", "property exclusions"],
    }),
    "returns/coverageExcludedVariantMetafields": setting({
      label: "Exclude variant metafields",
      description:
        "Coverage will not be offered for variants matching these metafields.",
      synonyms: ["exclude variant metafields", "metafield exclusions"],
    }),
    coverageExcludeDiscountCodes: setting({
      label: "Exclude product discount codes",
      description:
        "Coverage will not be offered when these discount codes are used.",
      synonyms: ["exclude discount codes", "discount code exclusions"],
    }),
  }),
  ...coverageGroup("coverage.return_coverage", {
    coverage: setting({
      label: "Covered return methods",
      description:
        "Which return methods (store credit, exchange, refund) are covered by return coverage.",
      synonyms: ["covered return methods", "coverage methods"],
    }),
    "returns/finalSaleReturns/enabled": setting({
      label: "Final sale returns coverage",
      description:
        "Allow customers to purchase coverage for final sale items, making them eligible for returns.",
      synonyms: ["final sale returns coverage", "final sale coverage"],
    }),
    "publicapi/discountsAreFinalSales": setting({
      label: "Make sale items ineligible",
      description:
        "Products with a sale (compare-at) price set are ineligible for returns coverage (CommentSold only).",
      synonyms: ["make sale items ineligible", "discounts are final sales"],
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "comment-sold",
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Only rendered for stores with the CommentSold integration connected.",
      },
    }),
    "publicapi/coverageExcludeLocalPickup": setting({
      label: "Exclude in-store pickup (CommentSold only)",
      description:
        "Do not add coverage for in-store pickup orders on your CommentSold store.",
      synonyms: [
        "commentsold local pickup",
        "commentsold in store pickup",
        "exclude local pickup",
      ],
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "comment-sold",
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Only rendered for stores with the CommentSold integration connected.",
      },
    }),
    "publicapi/couponedItemsDiscountThreshold": setting({
      label: "Couponed items threshold",
      description:
        "Couponed items discounted above this percentage are treated as final sale and not covered (CommentSold only).",
      synonyms: ["couponed items threshold", "coupon discount threshold"],
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "comment-sold",
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Only rendered for stores with the CommentSold integration connected.",
      },
    }),
    coverageExcludeProductTags: setting({
      label: "Exclude product tags",
      description:
        "Coverage will not be offered for products with these Shopify tags.",
      synonyms: [
        "exclude product tags from coverage",
        "coverage product tag exclusions",
      ],
    }),
    coverageExcludeCollections: setting({
      label: "Exclude collections",
      description:
        "Coverage will not be offered for products in these collections.",
      synonyms: [
        "exclude collections from coverage",
        "coverage collection exclusions",
      ],
    }),
    "returns/returnsCoverageExcludedSkus": setting({
      label: "Exclude SKUs",
      description: "Coverage will not be offered for products with these SKUs.",
      synonyms: ["exclude skus", "sku exclusions"],
    }),
    coverageExcludeCentEnding: setting({
      label: "Exclude cent endings",
      description:
        "Coverage will not be offered for products whose pre-discount price ends in these cent values.",
      synonyms: ["exclude cent endings", "cent ending exclusions"],
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "comment-sold",
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Only rendered for stores with the CommentSold integration connected.",
      },
    }),
  }),
  ...coverageGroup("coverage.checkout_ui_customizations", {
    "shopifyExtension/checkoutUICustomizations/titleTextSize": setting({
      label: "Title text size",
      description:
        "Text size of the coverage element title in Shopify checkout.",
      synonyms: ["title text size", "checkout title size"],
    }),
    "shopifyExtension/checkoutUICustomizations/subtextSize": setting({
      label: "Subtext size",
      description:
        "Text size of the coverage element subtext in Shopify checkout.",
      synonyms: ["subtext size", "checkout subtext size"],
    }),
    "shopifyExtension/checkoutUICustomizations/spacing": setting({
      label: "Spacing above/below element",
      description:
        "Spacing and dividers above and below the coverage element in Shopify checkout.",
      synonyms: ["checkout spacing", "element spacing"],
    }),
  }),
  ...coverageGroup("coverage.display_options", {
    html: setting({
      label: "Info text",
      description:
        "The coverage info text shown next to the coverage toggle on the product page.",
      synonyms: ["info text", "coverage text"],
    }),
    checkboxFontSize: setting({
      label: "Font size",
      description:
        "Font size, in pixels, of the coverage text on the product page.",
      synonyms: ["font size", "coverage font size"],
    }),
    checkboxSelector: setting({
      label: "CSS selector",
      description:
        "CSS selector for placing the Redo coverage info on the product detail page.",
      synonyms: ["css selector", "coverage selector", "placement selector"],
    }),
  }),
  ...coverageGroup("coverage.pre_protected_orders", {
    preProtectedOrdersEnabled: setting({
      label: "Pre-protected orders",
      description:
        "Automatically add return coverage to eligible orders placed without it.",
      synonyms: ["pre-protected orders", "retroactive coverage"],
    }),
  }),
  ...coverageGroup("coverage.advanced_coverage", {
    "shopifyExtension/variantNaming": setting({
      label: "Variant Naming",
      description:
        "Choose whether Shopify coverage variants show prices, five-character codes, or a custom name.",
      synonyms: ["coverage variant naming", "Shopify coverage variant names"],
    }),
    "shopifyExtension/deleteCartProperties": setting({
      label: "Delete Redo cart properties from orders",
      description:
        "Remove the cart properties Redo adds during checkout from each Shopify order after it is created.",
      synonyms: ["delete cart properties", "cart properties"],
    }),
  }),
};

type CoverageFlatTeamSettingKey = keyof typeof CoverageRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const CoverageFlatTeamSettingKeys = Object.keys(
  CoverageRegistry,
) as CoverageFlatTeamSettingKey[];

export const CoverageFlatTeamSettingCatalogDefinitions =
  CoverageFlatTeamSettingKeys.map((key) => {
    const definition = CoverageRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: CoverageFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    CoverageFlatTeamSettingKey,
    CoverageFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<CoverageFlatTeamSettingKey>
  >[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const CoverageFlatTeamSettingMetadataByKey = Object.fromEntries(
  CoverageFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<CoverageFlatTeamSettingKey, SettingMetadata>;
